import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Bell, Loader2, Send, Smartphone } from "lucide-react";
import api from "../lib/api";
import { enablePush, needsIOSInstall, pushSupported } from "../lib/push";

const STAGES = [
  ["CAVE_PROFILE", "Caverna"], ["LIVE_PRETEST", "Pré-Teste"], ["LIVE_TEST", "Teste"],
  ["LIVE_PRESCALE", "Pré-Escala"], ["LIVE_SCALE", "Escala"],
];
const CAVE_STAGES = ["CAVE_PROFILE", "CAVE_PAGE", "CAVE_WHATSAPP", "CAVE_ZAPDATA", "CAVE_CREATIVE"];
const DESTINATIONS = [
  ["/", "Início"], ["/jornada", "Jornada"], ["/agenda", "Agenda"],
  ["/operacao", "Operação"], ["/biblioteca", "Biblioteca"], ["/perfil", "Perfil"],
];
const KIND_LABELS = {
  manual: "Manual", announcement: "Comunicado", agenda_reminder: "Lembrete Agenda",
  phase_unlocked: "Fase liberada", inactivity: "Inatividade", health_critical: "Saúde crítica",
};
const inputCls = "w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF6B00]";

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick}
    className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${
      active ? "bg-[#FF6B00] border-[#FF6B00] text-black" : "border-[#3E4760] text-gray-300 hover:border-[#FF6B00]"
    }`}>
    {children}
  </button>
);

function StudentPicker({ mentorshipId, selected, onChange }) {
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get(`/api/push/students?mentorship_id=${mentorshipId}`).then((r) => setStudents(r.data.students)).catch(() => setStudents([]));
  }, [mentorshipId]);
  const list = students.filter((s) => `${s.name} ${s.email}`.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="space-y-2">
      <input data-testid="push-student-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar aluno..." className={inputCls} />
      <div className="max-h-48 overflow-y-auto space-y-1 pr-1">
        {list.map((s) => (
          <label key={s.id} className="flex items-center gap-2 text-xs text-gray-200 py-1">
            <input type="checkbox" data-testid={`push-student-${s.id}`} checked={selected.includes(s.id)} onChange={() => toggle(s.id)} className="accent-[#FF6B00]" />
            <span className="flex-1 truncate">{s.name}</span>
            <span className={`text-[10px] ${s.has_push ? "text-green-400" : "text-gray-500"}`}>{s.has_push ? "push ativo" : "sem push"}</span>
          </label>
        ))}
        {!list.length && <p className="text-[11px] text-gray-500">Nenhum aluno encontrado.</p>}
      </div>
    </div>
  );
}

function PushHistory({ version }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/api/push/history").then((r) => setItems(r.data.items)).catch(() => undefined); }, [version]);
  return (
    <div data-testid="push-history" className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4 space-y-2">
      <p className="text-white font-extrabold text-sm">Histórico de envios</p>
      {!items.length && <p className="text-xs text-gray-500">Nenhuma notificação enviada ainda.</p>}
      {items.map((i) => (
        <div key={i.id} data-testid={`push-history-item-${i.id}`} className="border-t border-[#282D3D] pt-2 text-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="text-white font-bold truncate">{i.title}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1F2330] text-gray-300 shrink-0">{KIND_LABELS[i.kind] || i.kind}</span>
          </div>
          <p className="text-gray-400 truncate">{i.body}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">
            {new Date(i.created_at).toLocaleString("pt-BR")} · {i.audience_label || "—"} · {i.reached_users}/{i.recipients} alunos receberam
            {i.failed ? ` · ${i.failed} falha(s)` : ""}{i.opted_out ? ` · ${i.opted_out} desativaram` : ""}
          </p>
        </div>
      ))}
    </div>
  );
}

export const PushComposer = ({ mentorships, isOwner }) => {
  const [form, setForm] = useState({ title: "", body: "", url: "/" });
  const [allMentorships, setAllMentorships] = useState(false);
  const [mentorshipIds, setMentorshipIds] = useState([]);
  const [mode, setMode] = useState("all"); // all | students
  const [userIds, setUserIds] = useState([]);
  const [stages, setStages] = useState([]);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (mentorships.length === 1 && !mentorshipIds.length) setMentorshipIds([mentorships[0].id]);
  }, [mentorships, mentorshipIds.length]);

  const audience = useMemo(() => ({
    all_mentorships: allMentorships,
    mentorship_ids: allMentorships ? [] : mentorshipIds,
    user_ids: mode === "students" ? userIds : [],
    stages: stages.flatMap((s) => (s === "CAVE_PROFILE" ? CAVE_STAGES : [s])),
  }), [allMentorships, mentorshipIds, mode, userIds, stages]);

  const refreshPreview = useCallback(() => {
    if (!audience.all_mentorships && !audience.mentorship_ids.length) return setPreview(null);
    if (mode === "students" && !userIds.length) return setPreview({ students: 0, with_push: 0 });
    api.post("/api/push/audience", audience).then((r) => setPreview(r.data)).catch(() => setPreview(null));
  }, [audience, mode, userIds.length]);
  useEffect(() => { refreshPreview(); }, [refreshPreview]);

  const toggleIn = (list, setList, id) => setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const singleMentorship = !allMentorships && mentorshipIds.length === 1 ? mentorshipIds[0] : null;

  const send = async () => {
    if (!window.confirm(`Enviar para ${preview?.students ?? 0} aluno(s)? (${preview?.with_push ?? 0} com push ativo)`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/api/push/send", { ...audience, ...form });
      toast.success(`Enviado: ${data.reached_users}/${data.recipients} alunos receberam no celular.`);
      setForm({ title: "", body: "", url: "/" });
      setVersion((v) => v + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar notificação.");
    } finally {
      setSending(false);
    }
  };

  const [testing, setTesting] = useState(false);
  const sendTest = async () => {
    setTesting(true);
    const payload = { title: form.title, body: form.body, url: form.url };
    try {
      try {
        await api.post("/api/push/test", payload);
      } catch (err) {
        if (err?.response?.status !== 400) throw err;
        if (needsIOSInstall()) throw new Error("ios_install");
        if (!pushSupported()) throw new Error("unsupported");
        await enablePush(); // no device yet → activate this one and retry
        await api.post("/api/push/test", payload);
      }
      toast.success("Teste enviado para o seu aparelho. Confira a notificação!");
    } catch (err) {
      const msg = {
        ios_install: "No iPhone, adicione o app à Tela de Início (Safari → Compartilhar) e tente de novo.",
        unsupported: "Este navegador não suporta notificações push.",
        permission_denied: "Permissão negada. Libere as notificações nas configurações do navegador.",
      }[err?.message];
      toast.error(msg || err?.response?.data?.detail || "Não foi possível enviar o teste.");
    } finally {
      setTesting(false);
    }
  };

  const canTest = form.title.trim() && form.body.trim() && !testing;
  const canSend = form.title.trim() && form.body.trim() && preview?.students > 0 && !sending;

  return (
    <div className="space-y-4">
      <div data-testid="push-composer" className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2 text-white font-extrabold text-sm">
          <Bell className="w-4 h-4 text-[#FF6B00]" /> Enviar notificação push
        </div>
        <input data-testid="push-title-input" maxLength={80} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Título (ex.: Aula ao vivo hoje às 20h)" className={inputCls} />
        <textarea data-testid="push-body-input" maxLength={240} rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Mensagem" className={inputCls} />
        <div>
          <p className="text-[11px] font-bold text-gray-400 mb-1">Ao tocar, abrir:</p>
          <select data-testid="push-url-select" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className={inputCls}>
            {DESTINATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>

        <div className="space-y-2">
          <p className="text-[11px] font-bold text-gray-400">Mentoria(s)</p>
          <div className="flex flex-wrap gap-1.5">
            {isOwner && <Chip testId="push-all-mentorships" active={allMentorships} onClick={() => { setAllMentorships(!allMentorships); setMode("all"); }}>Todas as mentorias</Chip>}
            {!allMentorships && mentorships.map((m) => (
              <Chip key={m.id} testId={`push-mentorship-${m.id}`} active={mentorshipIds.includes(m.id)}
                onClick={() => { toggleIn(mentorshipIds, setMentorshipIds, m.id); setUserIds([]); }}>{m.name}</Chip>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-[11px] font-bold text-gray-400">Público</p>
          <div className="flex flex-wrap gap-1.5">
            <Chip testId="push-mode-all" active={mode === "all"} onClick={() => setMode("all")}>Todos os alunos</Chip>
            <Chip testId="push-mode-students" active={mode === "students"} onClick={() => singleMentorship ? setMode("students") : toast.info("Selecione uma única mentoria para escolher alunos.")}>Alunos específicos</Chip>
          </div>
          {mode === "students" && singleMentorship && <StudentPicker mentorshipId={singleMentorship} selected={userIds} onChange={setUserIds} />}
        </div>

        <div className="space-y-2">
          <p className="text-[11px] font-bold text-gray-400">Filtrar por fase (opcional)</p>
          <div className="flex flex-wrap gap-1.5">
            {STAGES.map(([v, l]) => <Chip key={v} testId={`push-stage-${v}`} active={stages.includes(v)} onClick={() => toggleIn(stages, setStages, v)}>{l}</Chip>)}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-[#282D3D]">
          <span data-testid="push-audience-preview" className="text-[11px] text-gray-300">
            {preview ? `${preview.students} aluno(s) · ${preview.with_push} com push ativo` : "Selecione o público"}
          </span>
          <div className="flex gap-2">
          <button data-testid="push-test-self-btn" onClick={sendTest} disabled={!canTest}
            title="Envia só para você, para conferir antes de disparar"
            className="px-3 py-2 rounded-lg border border-[#3E4760] text-gray-200 hover:border-[#FF6B00] font-bold text-xs flex items-center gap-1.5 disabled:opacity-50 transition-colors">
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Smartphone className="w-3.5 h-3.5" />} Testar no meu aparelho
          </button>
          <button data-testid="push-send-btn" onClick={send} disabled={!canSend}
            className="px-4 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase flex items-center gap-1.5 disabled:opacity-50 transition-colors">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Enviar
          </button>
          </div>
        </div>
      </div>
      <PushHistory version={version} />
    </div>
  );
};
